/**
 * router/index.jsx — History API router (production-grade)
 *
 * Exports:
 *   RouterProvider  — wrap your app
 *   usePathname     — current pathname string
 *   useNavigate     — navigate(path) function
 *   Link            — <a> that uses pushState for internal links
 *   NavLink         — Link with active-state aria + styling
 *   matchRoute      — match pathname against a single pattern → { params } | null
 *   matchRoutes     — match pathname against a route table → { component, params } | null
 *   slugify         — text → url-safe slug
 *   NotFound        — 404 component
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  forwardRef,
} from 'react';

const RouterCtx = createContext(null);

/** Strip trailing slash; preserve bare '/'. */
const normalise = (p) => (p.length > 1 ? p.replace(/\/$/, '') : p);

const scrollTop = () => window.scrollTo({ top: 0, behavior: 'instant' });

// ── Slugify ───────────────────────────────────────────────────────────────────
export const slugify = (text) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

// ── Provider ──────────────────────────────────────────────────────────────────
export function RouterProvider({ children }) {
  const [pathname, setPathname] = useState(
    typeof window !== 'undefined' ? normalise(window.location.pathname) : '/'
  );

  useEffect(() => {
    const onPop = () => {
      setPathname(normalise(window.location.pathname));
      scrollTop();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((path) => {
    if (typeof window === 'undefined') return;
    const norm = normalise(path);
    window.history.pushState(null, '', norm);
    setPathname(norm);
    scrollTop();
  }, []);

  return (
    <RouterCtx.Provider value={{ pathname, navigate }}>
      {children}
    </RouterCtx.Provider>
  );
}

// ── Hooks ─────────────────────────────────────────────────────────────────────
export function usePathname() {
  return useContext(RouterCtx)?.pathname ?? '/';
}

export function useNavigate() {
  const ctx = useContext(RouterCtx);
  if (!ctx && process.env.NODE_ENV === 'development') {
    console.warn('[Router] useNavigate used outside <RouterProvider>');
  }
  return ctx?.navigate ?? (() => {});
}

// ── Link ──────────────────────────────────────────────────────────────────────
export const Link = forwardRef(function Link(
  { href, children, className, style, onClick, ...rest },
  ref
) {
  const navigate = useNavigate();

  const handleClick = (e) => {
    onClick?.(e);
    if (e.defaultPrevented) return;
    if (
      e.ctrlKey || e.metaKey || e.shiftKey || e.altKey ||
      e.button !== 0 ||
      !href ||
      href.startsWith('http') ||
      href.startsWith('mailto:') ||
      href.startsWith('tel:')
    ) return;
    e.preventDefault();
    navigate(href);
  };

  return (
    <a ref={ref} href={href} className={className} style={style} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
});

// ── NavLink ───────────────────────────────────────────────────────────────────
export const NavLink = forwardRef(function NavLink(
  { href, children, className, activeClassName = 'active', style, activeStyle, ...rest },
  ref
) {
  const pathname = usePathname();
  const isActive = normalise(pathname) === normalise(href ?? '');

  return (
    <Link
      ref={ref}
      href={href}
      className={[className, isActive ? activeClassName : ''].filter(Boolean).join(' ')}
      style={{ ...style, ...(isActive ? activeStyle : {}) }}
      aria-current={isActive ? 'page' : undefined}
      {...rest}
    >
      {children}
    </Link>
  );
});

// ── Route matching ────────────────────────────────────────────────────────────
export function matchRoute(pattern, pathname) {
  const pp = pattern.split('/');
  const up = normalise(pathname).split('/');
  if (pp.length !== up.length) return null;

  const params = {};
  for (let i = 0; i < pp.length; i++) {
    if (pp[i].startsWith(':')) {
      params[pp[i].slice(1)] = decodeURIComponent(up[i]);
    } else if (pp[i] !== up[i]) {
      return null;
    }
  }
  return { params };
}

export function matchRoutes(routes, pathname) {
  const norm = normalise(pathname);
  for (const route of routes) {
    const m = matchRoute(route.path, norm);
    if (m) return { ...route, params: m.params };
  }
  return null;
}

// ── NotFound ──────────────────────────────────────────────────────────────────
export function NotFound() {
  return (
    <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
      <h1 style={{ fontSize: '6rem', margin: 0, opacity: 0.15 }}>404</h1>
      <p style={{ fontSize: '1.25rem', marginBottom: '2rem' }}>Page not found</p>
      <NavLink href="/" style={{ textDecoration: 'underline' }}>← Back home</NavLink>
    </div>
  );
}